<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  LAIS_PAGE_PADDING_CLASS,
  LAIS_PAGE_SHELL_CLASS,
} from 'dashboard/helper/laisClassNames';

import NotificationTable from './NotificationTable.vue';
import NotificationPreferences from '../../settings/profile/NotificationPreferences.vue';
import SubscriptionsManagement from './SubscriptionsManagement.vue';

import { ACCOUNT_EVENTS } from '../../../../helper/AnalyticsHelper/events';
export default {
  components: {
    LaisHeader,
    LaisCard,
    NotificationTable,
    NotificationPreferences,
    SubscriptionsManagement,
    TableFooter,
  },
  setup() {
    return {
      LAIS_PAGE_PADDING_CLASS,
      LAIS_PAGE_SHELL_CLASS,
    };
  },
  data() {
    return {
      tab: ['preferencias', 'alertas'].includes(this.$route.query.tab)
        ? this.$route.query.tab
        : 'caixa',
      sortOrder: ['asc', 'desc'].includes(this.$route.query.notification_sort)
        ? this.$route.query.notification_sort
        : 'desc',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      getFilteredNotifications: 'notifications/getFilteredNotifications',
      meta: 'notifications/getMeta',
      uiFlags: 'notifications/getUIFlags',
    }),
    records() {
      return this.getFilteredNotifications({ sortOrder: this.sortOrder });
    },
    canManageLeadConsent() {
      return this.currentRole === 'administrator';
    },
  },
  watch: {
    '$route.query.tab'(value) {
      this.tab = ['preferencias', 'alertas'].includes(value) ? value : 'caixa';
    },
  },
  mounted() {
    this.fetchNotifications(Number(this.$route.query.page) || 1);
  },
  methods: {
    fetchNotifications(page = 1) {
      this.$store.dispatch('notifications/index', {
        page,
        sortOrder: this.sortOrder,
      });
    },
    syncInboxQuery(page = 1) {
      const query = { ...this.$route.query };
      if (page > 1) {
        query.page = String(page);
      } else {
        delete query.page;
      }

      if (this.sortOrder === 'desc') {
        delete query.notification_sort;
      } else {
        query.notification_sort = this.sortOrder;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    setTab(nextTab) {
      this.tab = nextTab;
      const query = { ...this.$route.query };
      if (nextTab !== 'alertas') {
        delete query.alert_id;
        delete query.alert_page;
        delete query.alert_status;
      }
      if (nextTab === 'caixa') {
        delete query.tab;
      } else {
        query.tab = nextTab;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    onPageChange(page) {
      this.syncInboxQuery(page);
      this.fetchNotifications(page);
    },
    onSortChange(nextSortOrder) {
      if (this.sortOrder === nextSortOrder) return;

      this.sortOrder = nextSortOrder;
      this.syncInboxQuery(1);
      this.fetchNotifications(1);
    },
    openConversation(notification) {
      const {
        primary_actor_id: primaryActorId,
        primary_actor_type: primaryActorType,
        primary_actor: { id: conversationId },
        notification_type: notificationType,
      } = notification;

      useTrack(ACCOUNT_EVENTS.OPEN_CONVERSATION_VIA_NOTIFICATION, {
        notificationType,
      });
      this.$store.dispatch('notifications/read', {
        id: notification.id,
        primaryActorId,
        primaryActorType,
        unreadCount: this.meta.unreadCount,
      });

      this.$router.push(
        `/app/accounts/${this.accountId}/conversations/${conversationId}`
      );
    },
    markNotificationAsRead(notification) {
      const {
        id,
        primary_actor_id: primaryActorId,
        primary_actor_type: primaryActorType,
      } = notification;

      this.$store
        .dispatch('notifications/read', {
          id,
          primaryActorId,
          primaryActorType,
          unreadCount: this.meta.unreadCount,
        })
        .then(() => this.$store.dispatch('notifications/unReadCount'));
    },
    markNotificationAsUnread(notification) {
      this.$store
        .dispatch('notifications/unread', { id: notification.id })
        .then(() => this.$store.dispatch('notifications/unReadCount'));
    },
    snoozeNotification(notification) {
      const snoozedUntil = Math.floor(Date.now() / 1000) + 24 * 60 * 60;
      this.$store
        .dispatch('notifications/snooze', {
          id: notification.id,
          snoozedUntil,
        })
        .then(() =>
          useAlert(this.$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.SNOOZED'))
        );
    },
    deleteNotification(notification) {
      this.$store
        .dispatch('notifications/delete', {
          notification,
          count: this.meta.count,
          unreadCount: this.meta.unreadCount,
        })
        .then(() => this.$store.dispatch('notifications/unReadCount'));
    },
    onMarkAllDoneClick() {
      useTrack(ACCOUNT_EVENTS.MARK_AS_READ_NOTIFICATIONS);
      this.$store.dispatch('notifications/readAll');
    },
    openConsentCenter() {
      this.$router.push({
        name: 'minha_lais_index',
        params: { accountId: this.accountId },
        query: { tab: 'notificacoes' },
      });
    },
  },
};
</script>

<template>
  <section :class="LAIS_PAGE_SHELL_CLASS">
    <LaisHeader
      :title="$t('NOTIFICATIONS_PAGE.TITLE')"
      :helper="$t('NOTIFICATIONS_PAGE.HELPER')"
    />
    <div
      :class="`flex h-full flex-col overflow-hidden ${LAIS_PAGE_PADDING_CLASS}`"
    >
      <div class="mb-4 flex items-center gap-2 overflow-x-auto">
        <button
          type="button"
          class="rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
          :class="
            tab === 'caixa'
              ? 'border-lais-primary bg-lais-primary text-white'
              : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
          "
          @click="setTab('caixa')"
        >
          {{ $t('NOTIFICATIONS_PAGE.TABS.INBOX') }}
        </button>
        <button
          type="button"
          class="rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
          :class="
            tab === 'preferencias'
              ? 'border-lais-primary bg-lais-primary text-white'
              : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
          "
          @click="setTab('preferencias')"
        >
          {{ $t('NOTIFICATIONS_PAGE.TABS.PREFERENCES') }}
        </button>
        <button
          type="button"
          class="rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
          :class="
            tab === 'alertas'
              ? 'border-lais-primary bg-lais-primary text-white'
              : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
          "
          @click="setTab('alertas')"
        >
          {{ $t('NOTIFICATIONS_PAGE.TABS.CREATED_ALERTS') }}
        </button>
      </div>

      <template v-if="tab === 'caixa'">
        <NotificationTable
          :notifications="records"
          :is-loading="uiFlags.isFetching"
          :is-updating="uiFlags.isUpdating"
          :sort-order="sortOrder"
          :on-click-notification="openConversation"
          :on-mark-all-done-click="onMarkAllDoneClick"
          :on-sort-change="onSortChange"
          :on-mark-read-notification="markNotificationAsRead"
          :on-mark-unread-notification="markNotificationAsUnread"
          :on-snooze-notification="snoozeNotification"
          :on-delete-notification="deleteNotification"
        />
        <TableFooter
          class="rounded-b-lais border border-t-0 border-lais-border bg-white px-4 shadow-lais-card sm:px-6"
          :current-page="Number(meta.currentPage)"
          :total-count="meta.count"
          :page-size="15"
          @page-change="onPageChange"
        />
      </template>

      <div
        v-else-if="tab === 'preferencias'"
        class="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_320px]"
      >
        <LaisCard :title="$t('LEAD_ALERTS.VIEW.USER_PREFERENCES_TITLE')">
          <p class="mb-4 text-[13px] leading-6 text-lais-text-muted">
            {{ $t('LEAD_ALERTS.VIEW.USER_PREFERENCES_HELPER') }}
          </p>
          <NotificationPreferences />
        </LaisCard>

        <LaisCard :title="$t('LEAD_ALERTS.VIEW.COMMERCIAL_OPERATION_TITLE')">
          <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
            <p>
              {{ $t('LEAD_ALERTS.VIEW.COMMERCIAL_OPERATION_HELPER') }}
            </p>
            <button
              v-if="canManageLeadConsent"
              type="button"
              class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
              @click="openConsentCenter"
            >
              <span class="i-lucide-arrow-up-right size-4" />
              {{ $t('LEAD_ALERTS.VIEW.OPEN_CONSENT_CENTER') }}
            </button>
            <a
              href="/lais/suporte/pt-BR"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
            >
              <span class="i-lucide-life-buoy size-4" />
              {{ $t('LEAD_ALERTS.VIEW.HELP_LINK') }}
            </a>
          </div>
        </LaisCard>
      </div>

      <SubscriptionsManagement
        v-else
        :highlight-alert-id="$route.query.alert_id"
      />
    </div>
  </section>
</template>
